import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useState } from 'react';

import { jobSurgeQueryKey } from '@/routes/Agency/PaySurge/hooks/useJobSurge';

import { dashboardV2Client } from '../api/client';
import { dashboardV2QueryKeys } from '../api/queryKeys';
import { ShiftHandlingResult } from '../api/types';
import { apiErrorMessage } from '../util/apiError';
import { minutesUntil, startsIn } from '../util/countdown';

import useDashboardToast from './useDashboardToast';

/**
 * Take over / Mark as resolved from the Shift Detail panel (GRAVY-10443).
 *
 * After either decision it invalidates every dashboard query, the
 * useTimesheetReview pattern: the panel footer, the card's badge and the toast
 * feed's `my_handlings` all move together. A refusal does the same, because a
 * 409 means the panel was showing a claim state that no longer exists.
 */
const useShiftHandling = (agencyId: number, shiftId: number) => {
  const queryClient = useQueryClient();
  const { notify } = useDashboardToast();
  const [pending, setPending] = useState<null | 'take_over' | 'resolve'>(null);
  const [error, setError] = useState<string | null>(null);

  const decide = useCallback(
    async (
      action: 'take_over' | 'resolve',
      run: () => Promise<ShiftHandlingResult>,
      failure: string,
    ): Promise<ShiftHandlingResult | null> => {
      setPending(action);
      setError(null);
      try {
        return await run();
      } catch (e) {
        setError(apiErrorMessage(e) ?? failure);
        return null;
      } finally {
        setPending(null);
        await queryClient.invalidateQueries({
          queryKey: dashboardV2QueryKeys.root,
        });
      }
    },
    [queryClient],
  );

  const takeOver = useCallback(
    async (startsAt?: string | null) => {
      const result = await decide(
        'take_over',
        () => dashboardV2Client.takeOverShift(agencyId, shiftId),
        'Could not take over this shift. Please try again.',
      );
      const expiresAt = result?.handling.expires_at;
      if (!result || !expiresAt) return;

      // Figma 40565:188693.
      const span = startsIn(startsAt);
      notify({
        key: `taken_over:${result.job_id}:${result.handling.claimed_at}`,
        tone: 'info',
        title: `You've taken over Shift #${result.job_id}`,
        message:
          `Shift still unfilled${span ? `, starts in ${span}` : ''}. ` +
          `Take action within ${minutesUntil(expiresAt)} min or it returns to the queue.`,
      });
    },
    [agencyId, shiftId, decide, notify],
  );

  const resolve = useCallback(async () => {
    const result = await decide(
      'resolve',
      () => dashboardV2Client.resolveShift(agencyId, shiftId),
      'Could not resolve this shift. Please try again.',
    );
    // Resolving also ends the job's running surge on the server (AC 16). The
    // surge cache sits outside the dashboard root, so mark it stale too, or
    // the banner and Manage surge pay show the pre-resolve schedule.
    if (result) {
      await queryClient.invalidateQueries({
        queryKey: jobSurgeQueryKey(result.job_id),
      });
    }
    return result;
  }, [agencyId, shiftId, decide, queryClient]);

  return { takeOver, resolve, pending, error };
};

export default useShiftHandling;
